import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard, CheckCircle, AlertCircle, Loader2,
  ShieldCheck, RefreshCw, ExternalLink, Package,
  Users, Briefcase, TrendingUp, HeadphonesIcon, Zap,
  Calendar, AlertTriangle, Sparkles,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { billingService, BillingStatus } from '../../services/billingService';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { showError } from '../../components/Alerts';

/* ─────────────────────────────────────────────────────────────
   Datos estáticos
───────────────────────────────────────────────────────────── */

const PLAN_FEATURES = [
  { icon: <Briefcase className="w-4 h-4" />, label: 'Proyectos y unidades funcionales ilimitadas' },
  { icon: <Package className="w-4 h-4" />, label: 'Módulo comercial completo (ventas, clientes, precios)' },
  { icon: <TrendingUp className="w-4 h-4" />, label: 'Contabilidad, flujo de caja y transacciones' },
  { icon: <CreditCard className="w-4 h-4" />, label: 'Compras, proveedores y órdenes de compra' },
  { icon: <Users className="w-4 h-4" />, label: 'Usuarios, roles y permisos sin límite' },
  { icon: <HeadphonesIcon className="w-4 h-4" />, label: 'Soporte prioritario incluido' },
  { icon: <Zap className="w-4 h-4" />, label: 'Actualizaciones automáticas' },
];

const MONTHLY_PRICE_CENTS = 100;   // $1.00 USD
const ANNUAL_PRICE_CENTS = 12000;  // $9.00 USD (aplica el descuento, poner precio total)
const CURRENCY = 'USD';

/* ─────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────── */

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-AR', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
};

const formatAmount = (cents: number | null, currency: string | null): string => {
  if (cents == null || currency == null) return '—';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(cents / 100);
};

const usd = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(cents / 100);

/* ─────────────────────────────────────────────────────────────
   Sub-componente: CheckIcon
───────────────────────────────────────────────────────── */

const FeatureCheck: React.FC<{ label: string }> = ({ label }) => (
  <li className="flex items-center gap-2.5 text-sm text-foreground/80">
    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/15 shrink-0">
      <CheckCircle className="w-3.5 h-3.5 text-primary" />
    </span>
    {label}
  </li>
);

/* ─────────────────────────────────────────────────────────────
   Sub-componente: PlanCard
───────────────────────────────────────────────────────── */

interface PlanCardProps {
  title: string;
  badge?: string;
  priceLabel: string;
  priceNote: string;
  savings?: string;
  highlighted?: boolean;
  onSubscribe: () => void;
  subscribing: boolean;
  isActive: boolean;
}

const PlanCard: React.FC<PlanCardProps> = ({
  title, badge, priceLabel, priceNote, savings,
  highlighted = false, onSubscribe, subscribing, isActive,
}) => {
  const base = highlighted
    ? 'relative flex flex-col rounded-2xl border-2 border-primary bg-primary/5 shadow-lg shadow-primary/10 overflow-hidden'
    : 'relative flex flex-col rounded-2xl border border-border bg-card shadow-sm overflow-hidden';

  return (
    <div className={base}>
      {/* Badge "Más popular" */}
      {badge && (
        <div className="absolute top-0 inset-x-0 flex justify-center">
          <span className="inline-flex items-center gap-1 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest px-4 py-1 rounded-b-lg shadow">
            <Sparkles className="w-3 h-3" />
            {badge}
          </span>
        </div>
      )}

      <div className={`flex flex-col flex-1 p-7 ${badge ? 'pt-10' : ''}`}>
        {/* Nombre del plan */}
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">{title}</p>

        {/* Precio */}
        <div className="mb-1">
          <span className="text-4xl font-extrabold text-foreground tracking-tight">{priceLabel}</span>
          <span className="text-sm text-muted-foreground ml-1">{CURRENCY}</span>
        </div>
        <p className="text-xs text-muted-foreground mb-1">{priceNote}</p>
        {savings && (
          <span className="inline-block mt-1 mb-4 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 px-2 py-0.5 rounded-full">
            {savings}
          </span>
        )}

        <div className="flex-1" />

        {/* Separador */}
        <div className="border-t border-border my-5" />

        {/* Features */}
        <ul className="space-y-2.5 mb-6">
          {PLAN_FEATURES.map((f, i) => (
            <FeatureCheck key={i} label={f.label} />
          ))}
        </ul>

        {/* CTA */}
        {isActive ? (
          <div className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border border-success/30 bg-success/8 text-success text-sm font-medium">
            <CheckCircle className="w-4 h-4 shrink-0" />
            Plan activo
          </div>
        ) : (
          <Button
            className={`w-full ${highlighted ? '' : 'variant-outline'}`}
            variant={highlighted ? 'default' : 'outline'}
            size="lg"
            onClick={onSubscribe}
            disabled={subscribing}
          >
            {subscribing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Redirigiendo…
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                Suscribirse
              </>
            )}
          </Button>
        )}

        <p className="text-[11px] text-center text-muted-foreground mt-3">
          Pago seguro · Stripe · Cancelá cuando quieras
        </p>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Página principal
───────────────────────────────────────────────────────── */

const BillingPage: React.FC = () => {
  const { isOwner } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscribingMonthly, setSubscribingMonthly] = useState(false);
  const [subscribingAnnual, setSubscribingAnnual] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  useEffect(() => {
    if (!isOwner()) navigate('/unauthorized', { replace: true });
  }, [isOwner, navigate]);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const data = await billingService.getStatus();
      setStatus(data);
    } catch {
      showError('No se pudo cargar el estado de la suscripción.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSubscribe = async (annual: boolean) => {
    if (annual) setSubscribingAnnual(true);
    else setSubscribingMonthly(true);
    try {
      const { url } = await billingService.createCheckoutSession(annual);
      window.location.href = url;
    } catch {
      showError('No se pudo iniciar el proceso de pago. Intentá nuevamente.');
    } finally {
      if (annual) setSubscribingAnnual(false);
      else setSubscribingMonthly(false);
    }
  };

  const handlePortal = async () => {
    setOpeningPortal(true);
    try {
      const { url } = await billingService.createPortalSession();
      window.location.href = url;
    } catch {
      showError('No se pudo abrir el portal de gestión. Intentá nuevamente.');
      setOpeningPortal(false);
    }
  };

  const isActive = status?.subscriptionStatus === 'ACTIVE';
  const willCancel = isActive && status?.cancelAtPeriodEnd === true;

  /* ── Loading state ─────────────────────────────────────── */
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Cargando información de suscripción…</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-extrabold tracking-tight text-foreground">
          Planes y facturación
        </h1>
        <p className="text-muted-foreground text-sm max-w-md mx-auto leading-relaxed">
          Elegí el plan que mejor se adapte a tu empresa. Sin costos ocultos, sin permanencia mínima.
        </p>
      </div>

      {/* ── Aviso cancelación programada ───────────────────── */}
      {willCancel && (
        <div className="flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/8 px-4 py-3">
          <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
          <p className="text-sm text-foreground">
            Tu suscripción se <strong>cancelará el {formatDate(status?.currentPeriodEnd ?? null)}</strong>.
            Podés reactivarla desde el portal de Stripe antes de esa fecha.
          </p>
        </div>
      )}

      {/* ── Estado hero (activo / inactivo) ────────────────── */}
      {isActive ? (
        <Card className="border-success/30 bg-success/5">
          <CardContent className="pt-5 pb-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-success/15 shrink-0">
                  <CheckCircle className="w-5 h-5 text-success" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-base font-semibold text-foreground">
                      {status?.planName ?? 'Plan PruneStone'}
                    </h2>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-success/15 text-success">
                      <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                      {willCancel ? 'Cancela al vencer' : 'Activa'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Todos los módulos disponibles.
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={fetchStatus} disabled={loading} className="shrink-0 self-start sm:self-center">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                Actualizar
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-warning/30 bg-warning/5">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-warning/15 shrink-0">
                <AlertCircle className="w-5 h-5 text-warning" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground">Sin suscripción activa</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Elegí un plan a continuación para acceder a todas las funcionalidades.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Grid de planes ─────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch">
        {/* Plan Mensual */}
        <PlanCard
          title="Mensual"
          priceLabel={usd(MONTHLY_PRICE_CENTS)}
          priceNote="por mes · facturado mensualmente"
          onSubscribe={() => handleSubscribe(false)}
          subscribing={subscribingMonthly}
          isActive={isActive}
        />

        {/* Plan Anual — destacado */}
        <PlanCard
          title="Anual"
          badge="Más popular"
          priceLabel={usd(Math.round(ANNUAL_PRICE_CENTS / 12))}
          priceNote="por mes · facturado anualmente"
          savings={`Ahorrás ${usd(MONTHLY_PRICE_CENTS * 12 - ANNUAL_PRICE_CENTS / 12)} al año`}
          highlighted
          onSubscribe={() => handleSubscribe(true)}
          subscribing={subscribingAnnual}
          isActive={isActive}
        />
      </div>

      {/* ── Sección de gestión (sólo si está activo) ───────── */}
      {isActive && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Detalles técnicos */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Detalles de facturación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {status?.amountCents != null && (
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">Monto cobrado</p>
                  <p className="text-sm font-semibold text-foreground">
                    {formatAmount(status.amountCents, status.currency)}
                  </p>
                </div>
              )}
              {status?.currentPeriodEnd && (
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {willCancel ? 'Vence el' : 'Próximo cobro'}
                  </p>
                  <p className="text-sm font-medium text-foreground">
                    {formatDate(status.currentPeriodEnd)}
                  </p>
                </div>
              )}
              {status?.stripeSubscriptionId && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">ID de suscripción</p>
                  <code className="text-xs font-mono bg-muted px-2 py-1 rounded block truncate">
                    {status.stripeSubscriptionId}
                  </code>
                </div>
              )}
              {status?.stripeCustomerId && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">ID de cliente</p>
                  <code className="text-xs font-mono bg-muted px-2 py-1 rounded block truncate">
                    {status.stripeCustomerId}
                  </code>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Portal Stripe */}
          <Card className="border-primary/20">
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 shrink-0">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-sm">Gestionar suscripción</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Cambiá tu método de pago, revisá facturas o cancelá el plan desde el portal seguro de Stripe.
                  </p>
                </div>
              </div>
              <Button className="w-full" onClick={handlePortal} disabled={openingPortal}>
                {openingPortal ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Abriendo portal…
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Ir al portal de Stripe
                  </>
                )}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Serás redirigido al portal seguro de Stripe.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BillingPage;
